import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  AxiosHeaders,
} from "axios";
import { retrieveAccessTokenFromRefreshTokenWithExpiration } from "../requests/ManagementRequests";
import { isTokenExpired, isTokenAboutToExpire } from "./JwtUtilization";
import { clearImpersonationState } from "./ImpersonationSessionUtilization";
import endpoints from "../config/endpoints.json";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";
import {
  classifyAiRefusal,
  dispatchAiRefusal,
} from "./AiRefusalUtilization";

interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

interface RefreshResult {
  data?: RefreshTokenResponse;
  err?: unknown;
}

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

const checkServerHealth = async (): Promise<boolean> => {
  try {
    const healthUrl = `${connection.base.management.url}${endpoints.management.actuator.health.context}`;
    const healthAxios = axios.create();
    await healthAxios.get(healthUrl, { timeout: 5000 });
    return true;
  } catch {
    return false;
  }
};

let basicRequestInterceptor: number | null = null;
let basicResponseInterceptor: number | null = null;
let refreshPromise: Promise<RefreshResult> | null = null;

const doRefresh = (): Promise<RefreshResult> => {
  if (!refreshPromise) {
    const refreshToken = localStorage.getItem("refreshToken");
    const userProfileId = localStorage.getItem("userProfileId");
    refreshPromise = retrieveAccessTokenFromRefreshTokenWithExpiration(
      refreshToken,
      userProfileId,
    ).finally(() => {
      refreshPromise = null;
    }) as Promise<RefreshResult>;
  }
  return refreshPromise;
};

const handleRefreshSuccess = (data: RefreshTokenResponse): void => {
  localStorage.setItem("accessToken", data.accessToken);
  localStorage.setItem("refreshToken", data.refreshToken);
  window.dispatchEvent(new CustomEvent("tokenRefreshed"));
};

const handleRefreshFailure = (): void => {
  if (basicRequestInterceptor !== null) {
    axios.interceptors.request.eject(basicRequestInterceptor);
  }
  if (basicResponseInterceptor !== null) {
    axios.interceptors.response.eject(basicResponseInterceptor);
  }
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  // The impersonation flags go with the token that justified them. An impersonated session carries no
  // refresh token by design, so this is the path its 30-minute expiry ALWAYS takes — and the flags carry
  // the target learner's real display name and role. Left behind, they painted a "you are being viewed as
  // X" band on the logged-out login screen, unauthenticated, for up to 12 hours (the stale sweep's horizon,
  // against a 30-minute token) — a disclosure to whoever next opened that browser.
  clearImpersonationState();
  window.location.href = "/";
};

/**
 * An access token safe to authenticate a NON-axios call with — today the websocket CONNECT frame,
 * which carries the bearer itself and so never passes through the request interceptor that keeps
 * every HTTP call fresh. Callers used to read `accessToken` straight out of localStorage and give up
 * when it was expired; after a 30-minute inactivity reap that is precisely the state it is in, so the
 * "Yeniden baglan" retry (and `useWebSocket`'s own activity handler) silently no-oped on the one
 * token they were guaranteed to find stale — a dead button with nothing in the console.
 *
 * The refresh goes through `doRefresh()`, so it shares the in-flight promise the interceptor uses and
 * a retry racing an ordinary request cannot mint two token pairs. Returns null when no usable token
 * can be produced at all: the caller must NOT open a socket the server will only reject.
 */
export const ensureUsableAccessToken = async (): Promise<string | null> => {
  const stillUsable = (token: string | null): string | null =>
    token && !isTokenExpired(token) ? token : null;

  const accessToken = localStorage.getItem("accessToken");
  if (accessToken && !isTokenAboutToExpire(accessToken)) {
    return accessToken;
  }
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken || isTokenExpired(refreshToken)) {
    return stillUsable(accessToken);
  }
  const { data, err } = await doRefresh();
  if (!err && data) {
    handleRefreshSuccess(data);
    return data.accessToken;
  }
  // Deliberately NOT `handleRefreshFailure()`: a websocket retry must never log the session out. A
  // genuinely rejected refresh token will be met by the response interceptor on the next HTTP call,
  // which owns that decision. And a refresh that failed on the network leaves the existing token in
  // place — if it has not actually expired yet it is still worth a CONNECT.
  return stillUsable(accessToken);
};

export const setAxiosInterceptor = (): void => {
  removeAxiosInterceptor();
  basicRequestInterceptor = axios.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      let accessToken = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");

      const accessNeedsRefresh =
        accessToken && isTokenAboutToExpire(accessToken);
      const refreshNeedsRenewal =
        refreshToken && isTokenAboutToExpire(refreshToken, 86400000);

      if (
        !refreshPromise &&
        (accessNeedsRefresh || refreshNeedsRenewal) &&
        refreshToken &&
        !isTokenExpired(refreshToken)
      ) {
        const { data, err } = await doRefresh();
        if (!err && data) {
          handleRefreshSuccess(data);
          accessToken = data.accessToken;
        }
      }

      const headers = AxiosHeaders.from(config.headers);
      headers.set("Authorization", `Bearer ${accessToken}`);
      config.headers = headers;
      return config;
    },
    (error: unknown) => {
      return Promise.reject(error);
    },
  );

  basicResponseInterceptor = axios.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError & { config?: InternalAxiosRequestConfig & { _retry?: boolean } }) => {
      const originalRequest = error.config;

      if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        const refreshToken = localStorage.getItem("refreshToken");

        if (!refreshToken || isTokenExpired(refreshToken)) {
          handleRefreshFailure();
          return Promise.reject(error);
        }

        const { data, err } = await doRefresh();

        if (err) {
          const errStatus = (err as AxiosError)?.response?.status;
          if (errStatus === 400 || errStatus === 401 || errStatus === 423) {
            handleRefreshFailure();
            return Promise.reject(err);
          }
          return Promise.reject(error);
        } else if (data) {
          handleRefreshSuccess(data);
          const headers = AxiosHeaders.from(originalRequest.headers);
          headers.set("Authorization", `Bearer ${data.accessToken}`);
          originalRequest.headers = headers;
          return axios.request(originalRequest);
        }
      } else if (
        !error.response ||
        error.response.status === 502 ||
        error.response.status === 503
      ) {
        const isServerUp = await checkServerHealth();
        if (!isServerUp) {
          window.dispatchEvent(new CustomEvent("serverUnreachable"));
        }
        return Promise.reject(error);
      }
      // A refusal the platform MEANT — a spent AI credit pool, a model shedding load, points the
      // learner does not have — is announced here so one app-level notice can explain it wherever it
      // happened. The rejection still travels: each screen keeps its own in-chat message, and this
      // only adds the ambient half (which window, and when it frees).
      const aiRefusalKind = classifyAiRefusal(error);
      if (aiRefusalKind) {
        dispatchAiRefusal({
          kind: aiRefusalKind,
          errorKey:
            typeof error.response?.data === "string" ? error.response.data : null,
          url: error.config?.url ?? null,
        });
      }
      return Promise.reject(error);
    },
  );
};

export const removeAxiosInterceptor = (): void => {
  if (basicRequestInterceptor !== null) {
    axios.interceptors.request.eject(basicRequestInterceptor);
    basicRequestInterceptor = null;
  }

  if (basicResponseInterceptor !== null) {
    axios.interceptors.response.eject(basicResponseInterceptor);
    basicResponseInterceptor = null;
  }
};
